import { Icon } from "@fluentui/react";
import { Card, tokens } from "@fluentui/react-components";
import { New16Regular } from "@fluentui/react-icons";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import careOKRsImage from "../assets/explore-support-images/CARE_OKRs.png";
import cosocImage from "../assets/explore-support-images/COSOC.png";
import escalationCRIImage from "../assets/explore-support-images/Escalation_CRI_Health.png";
import forecastImage from "../assets/explore-support-images/ForeCast.png";
import getHelpImage from "../assets/explore-support-images/Get_Help.png";
import inPortalProductImage from "../assets/explore-support-images/InPortal_Product.png";
import intensiveCareImage from "../assets/explore-support-images/Intensive_Care.png";
import mcsImage from "../assets/explore-support-images/MCS.png";
import pheImage from "../assets/explore-support-images/PHE.png";
import readinessCommunicationImage from "../assets/explore-support-images/Readiness_And_Communication.png";
import shsImage from "../assets/explore-support-images/SHS.png";
import slaCreditsImage from "../assets/explore-support-images/SLA_Credits.png";
import wsrImage from "../assets/explore-support-images/WSR.png";
import appAssureImage from "../assets/explore-support-images/App_Assure.png";
import { AppStyles } from "../components/SupportView/Styles/ExploreSupport.styles";
import { careSupportEmail, reportRequestFormLink } from "../constants/SupportView.constants";
import { logEvent, logPageView } from "../helper/AppInsightsHelper";
import { useGetExploreSupportDataQuery } from "../slices/ExploreProducts.slice";

const imageKeyMap: { [key: string]: string } = {
  SCN000: appAssureImage,
  SCN001: cosocImage,
  SCN002: getHelpImage,
  SCN003: intensiveCareImage,
  SCN004: mcsImage,
  SCN005: pheImage,
  SCN006: readinessCommunicationImage,
  SCN007: slaCreditsImage,
  SCN008: wsrImage,
  SCN009: escalationCRIImage,
  SCN010: careOKRsImage,
  SCN011: shsImage,
  SCN012: inPortalProductImage,
  SCN013: forecastImage
};

const ExploreSupport = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useGetExploreSupportDataQuery(null);

   const supportOptions = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data
      .slice()
      .sort((a: any, b: any) => {
        // Sort so that entries with reportsStatus === 'Present' come first
        if (a.reportsStatus === "Present" && b.reportsStatus !== "Present") return -1;
        if (a.reportsStatus !== "Present" && b.reportsStatus === "Present") return 1;
        // Secondary sort by scenarioID for deterministic ordering
        return a.scenarioID.localeCompare(b.scenarioID);
      })
      .map((item: any) => ({
        image: imageKeyMap[item.scenarioID] || "", // fallback to empty if not found
        title: item.scenarios,
        description: item.oneliner,
        reportsStatus: item.reportsStatus,
        primaryAction: {
          onClick: () => {
            logEvent(`${item.scenarios} category is viewed`, "Explore Support Page", "ExploreSupport");
            navigate(`/supportreports?category=${encodeURIComponent(item.scenarios)}`);
          }
        }
      }));
  }, [data, navigate]);

  useEffect(() => logPageView("Explore Support Page", window.location.href), []);

  return (
    <div className={AppStyles.minHeightScreen}>
      {/* Header */}
      <div style={{ display: "flex",direction:"rtl", width: "100%", justifyContent: "space-between" }}>
        <div
          style={{
            marginTop: "10px",
            marginRight: "16px",
            backgroundColor: "#0078D4",
            color: "#FFFFFF",
            padding: "4px 12px",
            borderRadius: "9999px",
            display: "inline-flex",
            alignItems: "center",
            fontSize: "12px",
            fontWeight: 500
          }}
        >
          <New16Regular fontSize={12} style={{ marginRight: 4 }} />
          <span>Beta</span>
        </div>
      </div>

      <div className={AppStyles.header}>
        <div className={AppStyles.headerContainer}>
          <div className={AppStyles.headerContent}>
            <p className={AppStyles.mainDescription}>
              Discover powerful tools and expert assistance to help you succeed. Choose the support scenario that best
              fits your needs.
            </p>
          </div>
        </div>
      </div>
      {/* Support Options */}
      <div className={AppStyles.supportOptionsContainer}>
        <div className={AppStyles.responsiveGrid}>
          {isLoading ? (
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 300
              }}
            >
              <div
                className="loader"
                style={{
                  border: "8px solid #f3f3f3",
                  borderTop: "8px solid #0078d4",
                  borderRadius: "50%",
                  width: 60,
                  height: 60,
                  animation: "spin 1s linear infinite"
                }}
              />
              <style>
                {`
            @keyframes spin {
              0% { transform: rotate(0deg);}
              100% { transform: rotate(360deg);}
            }
          `}
              </style>
            </div>
          ) : (
            supportOptions.map((option: any, index: any) => <SupportCard key={index} {...option} isError={isError} />)
          )}
        </div>
      </div>

      {/* Immediate Assistance */}
      <div className={AppStyles.immediateAssistance}>
        <ContactMethod />
      </div>
    </div>
  );
};

// Support Card Component
const SupportCard = ({ image, title, description, primaryAction, isError, reportsStatus }: any) => {
  if (isError) return <Card style={{ margin: tokens.spacingVerticalL }}>Error loading data</Card>;
  return (
    <div className={AppStyles.supportCard}>
      <div className={AppStyles.supportCardImageContainer}>
        <img src={image} alt={title} className={AppStyles.supportCardImage} />
      </div>
      <div className={AppStyles.supportCardContent}>
        <h3 className={AppStyles.supportCardTitle}>{title}</h3>
        <p className={AppStyles.supportCardDescription}>{description}</p>
        <div className={AppStyles.supportCardActions}>
          {reportsStatus === "Present" ? (
            <button onClick={primaryAction.onClick} className={AppStyles.supportCardPrimaryButton}>
              Explore More
              <Icon iconName="ChevronRight" style={{ marginLeft: "8px", fontSize: "16px" }} />
            </button>
          ) : (
            <button
              className={AppStyles.supportCardPrimaryButton}
              disabled
              style={{ opacity: 0.7, cursor: "not-allowed" }}
            >
              Coming Soon
              <Icon iconName="Clock" style={{ marginLeft: "8px", fontSize: "16px" }} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Contact Method Component
export const ContactMethod = () => {
  const contactMethods = [
    {
      title: "Add Report",
      description: "Raise a request here",
      icon: <Icon iconName="ReportAdd" style={{ fontSize: "32px" }} />,
      onClick: () => {
        window.open(reportRequestFormLink, "_blank", "noopener,noreferrer");
      }
    },
    {
      title: "Email Support",
      description: "Response within 3 hours",
      icon: <Icon iconName="MailSolid" style={{ fontSize: "32px" }} />,
      onClick: () => {
        window.location.href = `mailto:${careSupportEmail}`;
      }
    }
  ];
  return (
    <>
      <div className={AppStyles.immediateAssistanceContainer}>
        <div className={AppStyles.headerContent}>
          <div className={AppStyles.contactMethodsGrid}>
            {contactMethods.map((method, idx) => (
              <div key={idx} className={AppStyles.contactMethod}>
                <div className={AppStyles.contactMethodIconContainer}>
                  <div className={AppStyles.contactMethodIcon} onClick={method.onClick} style={{ cursor: "pointer" }}>
                    {method.icon}
                  </div>
                </div>
                <h4 className={AppStyles.contactMethodTitle}>{method.title}</h4>
                <p className={AppStyles.contactMethodDescription}>{method.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default ExploreSupport;
